// Proposal shared utilities — extracted from Proposals.jsx refactor
import { format } from "date-fns";
import { foldHiddenTaxIntoScopes, NO_MATERIAL_MSG, distributeByWeight } from "@/lib/hideTax";

// Money is rounded to the cent at the LINE level (what prints) and summed from there, so rows,
// totals, PDF and the converted WO always agree to the penny.
export const r2 = (n) => Math.round(((parseFloat(n) || 0) + Number.EPSILON) * 100) / 100;
import { libSnapshot } from "./grid/libraryDiff";

export const statusOptions = [
  { value: "draft", label: "Draft" },
  { value: "bid_submitted", label: "Submitted" },
  { value: "won", label: "Won" },
  { value: "lost", label: "Lost" },
  { value: "cancelled", label: "Cancelled" },
];

export const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
};

export const formatCurrencyWhole = (value) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

// ----- Helpers -----
export const COST_TYPES = [
  { value: "1", label: "Labor" },
  { value: "2", label: "Material" },
  { value: "3", label: "Subcontract" },
  { value: "4", label: "Freight" },
  { value: "5", label: "Equipment" },
  { value: "6", label: "Labor Burden" },
  { value: "7", label: "Other" },
];

export const deriveCostType = (item) => {
  if ((parseFloat(item.material_cost) || 0) > 0) return "2";
  if ((parseFloat(item.labor_hours) || 0) > 0) return "1";
  if ((parseFloat(item.freight) || 0) > 0) return "4";
  return "2"; // default Material
};

export const DEFAULT_EXCLUSIONS = [];

export const PROJECT_TYPES = [
  { value: "service", label: "Service" },
  { value: "service_contract", label: "Service Contract" },
  { value: "tm", label: "T&M" },
];

export const TM_QUALIFICATION =
  "This Work Order will be billed on a Time & Materials basis at standard labor rates plus materials, including all associated costs, freight, tax, overtime, and applicable markups.";

export const DEFAULT_TERMS = [
  "Payment due upon completion; failure to provide valid tax exemption at payment will result in tax charges",
];

// iter233 — module-level default freight GM% (from Settings → Default
// Rates). Pages call setDefaultFreightMargin once after loading settings
// so newLineItem/partToLineItem stamp new items with the current default.
let _defaultFreightMargin = 15;
export const setDefaultFreightMargin = (v) => {
  const f = parseFloat(v);
  if (Number.isFinite(f) && f >= 0 && f < 100) _defaultFreightMargin = f;
};
export const getDefaultFreightMargin = () => _defaultFreightMargin;

// iter254 — module-level default drive-time GM% (from Settings → Default
// Rates). Same wiring as freight: pages seed it once via
// setDefaultDriveTimeGm(settings.default_drive_time_margin) so every
// `newScope()` starts with a consistent billing rate.
let _defaultDriveTimeGm = 0;
export const setDefaultDriveTimeGm = (v) => {
  const f = parseFloat(v);
  if (Number.isFinite(f) && f >= 0 && f < 100) _defaultDriveTimeGm = f;
};
export const getDefaultDriveTimeGm = () => _defaultDriveTimeGm;

// Settings → Default Rates for NEW lines (labor $/hr is a company rate, so it wins over a library part's
// stored value; GM% defaults fill in only where the part has none). Pages seed it via setDefaultRates(settings).
let _defaultRates = { labor_cost: 40, labor_margin: 73.333, material_margin: 50 };
const _pct = (v, fallback) => { const f = parseFloat(v); return Number.isFinite(f) && f >= 0 && f < 100 ? f : fallback; };
export const setDefaultRates = (s = {}) => {
  const c = parseFloat(s.default_labor_cost);
  _defaultRates = {
    labor_cost: Number.isFinite(c) && c >= 0 ? c : _defaultRates.labor_cost,
    labor_margin: _pct(s.default_labor_margin, _defaultRates.labor_margin),
    material_margin: _pct(s.default_material_margin, _defaultRates.material_margin),
  };
  setDefaultFreightMargin(s.default_freight_margin);
  setDefaultDriveTimeGm(s.default_drive_time_margin);
};
export const getDefaultRates = () => ({ ..._defaultRates });

// Proposal-wide GM% defaults (builder rail → Margins). Blank/null = keep the library / system default.
export const marginDefault = (d, k) => {
  const v = d?.[k];
  const f = parseFloat(v);
  return v === "" || v == null || !Number.isFinite(f) ? null : f;
};
export const withDefaultMargins = (item, d) => {
  const m = marginDefault(d, "material"), l = marginDefault(d, "labor"), f = marginDefault(d, "freight");
  if (m == null && l == null && f == null) return item;
  return { ...item, ...(m != null && { material_margin: m }), ...(l != null && { labor_margin: l }), ...(f != null && { freight_margin: f }) };
};

export const newLineItem = () => ({
  id: crypto.randomUUID(),
  name: "",
  description: "",
  part_number: "",
  qty: 1,
  material_cost: 0,
  material_margin: _defaultRates.material_margin,
  freight: 0,
  freight_type: "unit",
  freight_margin: _defaultFreightMargin,
  labor_cost: _defaultRates.labor_cost,
  labor_margin: _defaultRates.labor_margin,
  labor_hours: 0,
  location: "",
  cost_type: "",
});

export const newScope = (name = "") => ({
  id: crypto.randomUUID(),
  name,
  line_items: [newLineItem()],
  travel: 0,
  travel_gm: 15,
  equipment: 0,
  equipment_gm: 15,
  disposal: 0,
  disposal_gm: 15,
  // iter254 — drive time defaults from Settings → Default Rates
  drive_time: 0,
  drive_time_gm: _defaultDriveTimeGm,
});

export const partToLineItem = (part) => ({
  id: crypto.randomUUID(),
  name: part.name || "",
  description: part.description || "",
  part_number: part.part_number || "",
  qty: 1,
  material_cost: part.part_cost || 0,
  material_margin: part.material_margin ?? _defaultRates.material_margin,
  freight: part.freight || 0,
  freight_type: "unit",
  freight_margin: part.freight_margin ?? _defaultFreightMargin,
  labor_cost: _defaultRates.labor_cost,
  labor_margin: part.labor_margin ?? _defaultRates.labor_margin,
  labor_hours: part.labor_hours || 0,
  location: "",
  manufacturer: part.manufacturer || "",
  cost_type: "",
  // iter235 — flat-rate service items carry their mode + unit sell price.
  pricing_mode: part.pricing_mode || "standard",
  flat_rate: parseFloat(part.flat_rate) || 0,
  // Window Care — subcontract pass-through cost per unit.
  subcontract_cost: parseFloat(part.subcontract_cost) || 0,
  // Library cost baseline at pricing time — later library changes raise a Review tag, never a price change.
  lib_ref: libSnapshot(part),
});

export const calcLineItemTotals = (item) => {
  // iter235 — flat-rate service items: unit sell = flat_rate exactly,
  // carried in the labor bucket (non-taxable, like other labor). Hours ×
  // labor_cost remain the internal cost basis.
  if (item.pricing_mode === "flat_rate" || item.pricing_mode === "subcontract") {
    const flat = parseFloat(item.flat_rate) || 0;
    const hours = parseFloat(item.labor_hours) || 0;
    const labC = parseFloat(item.labor_cost) || 0;
    const freightCost = parseFloat(item.freight) || 0;
    const frtGM = parseFloat(item.freight_margin) || 0;
    const freight = (frtGM > 0 && frtGM < 100) ? freightCost / (1 - frtGM / 100) : freightCost;
    const freightType = item.freight_type || "unit";
    const qty = parseInt(item.qty) || 1;
    const unitTotal = flat + (freightType === "unit" ? freight : 0);
    const totalFreightForItem = r2(freightType === "unit" ? freight * qty : freight);
    const totalFreightCostForItem = r2(freightType === "unit" ? freightCost * qty : freightCost);
    const extLab = r2(flat * qty);
    const grand = r2(extLab + totalFreightForItem);
    return { totalMat: 0, totalLab: flat, rate: 0, freight, freightType, unitTotal, grand, qty, hours, totalFreightForItem, totalFreightCostForItem, matC: 0, labC,
             extMat: 0, extLab, extFreight: totalFreightForItem, extMatCost: 0, extLabCost: r2(hours * labC * qty) };
  }
  const matC = parseFloat(item.material_cost) || 0;
  const matGM = parseFloat(item.material_margin) || 0;
  const totalMat = matGM >= 100 ? matC : matC / (1 - matGM / 100);
  const labC = parseFloat(item.labor_cost) || 0;
  const labGM = parseFloat(item.labor_margin) || 0;
  const rate = labGM >= 100 ? labC : Math.ceil(labC / (1 - labGM / 100));
  const hours = parseFloat(item.labor_hours) || 0;
  const totalLab = hours * rate;
  // iter233 — freight GM% (sell = cost / (1 - m)). 0/absent = pass-through
  // so legacy items keep their exact pricing.
  const freightCost = parseFloat(item.freight) || 0;
  const frtGM = parseFloat(item.freight_margin) || 0;
  const freight = (frtGM > 0 && frtGM < 100) ? freightCost / (1 - frtGM / 100) : freightCost;
  const freightType = item.freight_type || "unit";
  const qty = parseInt(item.qty) || 1;
  const unitTotal = totalMat + (freightType === "unit" ? freight : 0) + totalLab;
  const totalFreightForItem = r2(freightType === "unit" ? freight * qty : freight);
  const totalFreightCostForItem = r2(freightType === "unit" ? freightCost * qty : freightCost);
  // Extended (qty-inflated) figures rounded to the cent — the line as it prints.
  const extMat = r2(totalMat * qty);
  const extLab = r2(totalLab * qty);
  const grand = r2(extMat + extLab + totalFreightForItem);
  return { totalMat, totalLab, rate, freight, freightType, unitTotal, grand, qty, hours, totalFreightForItem, totalFreightCostForItem, matC, labC,
           extMat, extLab, extFreight: totalFreightForItem, extMatCost: r2(matC * qty), extLabCost: r2(labC * hours * qty) };
};

export const calcScopeCost = (cost, gmPct) => {
  const c = parseFloat(cost) || 0;
  const gm = parseFloat(gmPct) || 0;
  return c > 0 ? r2(gm >= 100 ? c : c / (1 - gm / 100)) : 0;
};

// Distribute the CC fee silently INTO each scope's material line items
// (option-a math: scope-by-scope, proportional to each line's billed
// material). Falls back to travel → equipment → disposal → drive time →
// first line if a scope has zero material. The returned `_scopes` array
// preserves original scope fields plus these enrichments used by all
// customer-facing views:
//   ._lines         — line items each carrying `_displayMaterial`,
//                     `_displayLabor`, `_displayTotal` (qty-inflated).
//   ._travelBilled / ._equipBilled / ._disposalBilled / ._driveBilled —
//                     scope add-ons (may include fee overflow when the
//                     scope has no material).
//   ._scopeTotal    — grand total after fee folded in.
// iter244 — Drive Time is NO LONGER folded into line-item labor: doing so
// distorted flat-rate line prices on the printed proposal. Drive time (and
// equipment/travel/disposal) now surface as aggregate lines in the totals
// summary instead of inside the scope tables.
export const applyCCFeeDistribution = (scopes, ccFeePct = 0) => {
  const feePct = Math.max(0, parseFloat(ccFeePct ?? 0) || 0) / 100;
  return (scopes || []).map(scope => {
    const rawLines = (scope.line_items || []).map(li => {
      const t = calcLineItemTotals(li);
      return { li, t, billedMat: t.extMat, billedLab: t.extLab, grand: t.grand };
    });
    const driveBilled = calcScopeCost(scope.drive_time, scope.drive_time_gm ?? 0);
    const travelBilled = calcScopeCost(scope.travel, scope.travel_gm ?? 15);
    const equipBilled = calcScopeCost(scope.equipment, scope.equipment_gm ?? 15);
    const disposalBilled = calcScopeCost(scope.disposal, scope.disposal_gm ?? 15);
    // CC fee base includes every add-on so the grand total matches
    // calcProposalTotals penny-for-penny (fee rounded per scope, split in whole cents).
    const scopePreFee = r2(rawLines.reduce((s, x) => s + x.grand, 0) + travelBilled + equipBilled + disposalBilled + driveBilled);
    const scopeFee = r2(scopePreFee * feePct);
    const matSum = rawLines.reduce((s, x) => s + x.billedMat, 0);
    const extras = (matSum > 0 && scopeFee > 0) ? distributeByWeight(rawLines.map(x => x.billedMat), scopeFee) : rawLines.map(() => 0);

    let fallbackTravel = travelBilled, fallbackEquip = equipBilled, fallbackDisp = disposalBilled, fallbackDrive = driveBilled;
    const enriched = rawLines.map((x, i) => {
      const extra = extras[i] || 0;
      return {
        ...x.li,
        _displayMaterial: r2(x.billedMat + extra),
        _displayLabor: x.billedLab,
        _displayFreight: x.t.totalFreightForItem || 0,
        _displayTotal: r2(x.grand + extra),
      };
    });
    if (matSum === 0 && scopeFee > 0) {
      if (fallbackTravel > 0) fallbackTravel = r2(fallbackTravel + scopeFee);
      else if (fallbackEquip > 0) fallbackEquip = r2(fallbackEquip + scopeFee);
      else if (fallbackDisp > 0) fallbackDisp = r2(fallbackDisp + scopeFee);
      else if (fallbackDrive > 0) fallbackDrive = r2(fallbackDrive + scopeFee);
      else if (enriched.length > 0) {
        enriched[0]._displayMaterial = r2(enriched[0]._displayMaterial + scopeFee);
        enriched[0]._displayTotal = r2(enriched[0]._displayTotal + scopeFee);
      }
    }
    const scopeTotal = r2(enriched.reduce((s, x) => s + (x._displayTotal || 0), 0) + fallbackTravel + fallbackEquip + fallbackDisp + fallbackDrive);
    return {
      ...scope,
      _lines: enriched,
      _travelBilled: fallbackTravel,
      _equipBilled: fallbackEquip,
      _disposalBilled: fallbackDisp,
      _driveBilled: fallbackDrive,
      _scopeTotal: scopeTotal,
    };
  });
};

// iter244 — aggregate display summary used by every customer-facing
// surface (preview, PDFs, public review page): the scope tables show
// line items ONLY; drive time / equipment / travel / disposal are summed
// across scopes and rendered as their own lines in the totals block.
export const summarizeProposalDisplay = (scopes, ccFeePct = 0, hiddenTax = 0) => {
  const ccScopes = applyCCFeeDistribution(scopes || [], ccFeePct);
  // iter310 — hide-tax document: fold the real tax into the material
  // figures BEFORE anything is summed, so the printed lines and the
  // grand total still add up to the internal total.
  if (hiddenTax > 0 && !foldHiddenTaxIntoScopes(ccScopes, hiddenTax)) {
    throw new Error(NO_MATERIAL_MSG);
  }
  const agg = { itemsSubtotal: 0, driveTime: 0, travel: 0, equipment: 0, disposal: 0 };
  ccScopes.forEach(sc => {
    agg.itemsSubtotal += (sc._lines || []).reduce((s, l) => s + (l._displayTotal || 0), 0);
    agg.driveTime += sc._driveBilled || 0;
    agg.travel += sc._travelBilled || 0;
    agg.equipment += sc._equipBilled || 0;
    agg.disposal += sc._disposalBilled || 0;
  });
  return { ccScopes, ...agg };
};

export const calcProposalTotals = (scopes, taxRate, ccFeePct = 0, discountType = "flat", discountValue = 0, taxOverride = null) => {
  let subtotal = 0;
  let totalMaterial = 0, totalFreight = 0, totalLabor = 0, totalEquipment = 0;
  let totalHours = 0, totalTravel = 0, totalDisposal = 0, totalDriveTime = 0;
  let costMaterial = 0, costFreight = 0, costLabor = 0, costEquipment = 0, costTravel = 0, costDisposal = 0;
  // Subcontract pass-through (Window Care): sell = flat_rate/unit, cost =
  // subcontract_cost/unit. Kept out of the labor bucket so the internal GM
  // table shows the real sub cost, and out of the tax base like labor.
  let totalSubcontract = 0, costSubcontract = 0;
  const ccFeeP = Math.max(0, parseFloat(ccFeePct ?? 0) || 0);
  let ccFeeAmount = 0;
  scopes.forEach(scope => {
    let scopePreFee = 0;
    (scope.line_items || []).forEach(item => {
      const t = calcLineItemTotals(item);
      totalMaterial += t.extMat;
      totalFreight += t.extFreight;
      if (item.pricing_mode === "subcontract") {
        totalSubcontract += t.extLab;
        costSubcontract += r2((parseFloat(item.subcontract_cost) || 0) * t.qty);
      } else {
        totalLabor += t.extLab;
        costLabor += t.extLabCost;
      }
      totalHours += (parseFloat(item.labor_hours) || 0) * t.qty;
      subtotal += t.grand;
      scopePreFee += t.grand;
      costMaterial += t.extMatCost;
      costFreight += t.totalFreightCostForItem;
    });
    const travelCost = r2(scope.travel);
    const travelBilled = calcScopeCost(scope.travel, scope.travel_gm ?? 15);
    totalTravel += travelBilled;
    costTravel += travelCost;
    subtotal += travelBilled;
    const eqCost = r2(scope.equipment);
    const eqBilled = calcScopeCost(scope.equipment, scope.equipment_gm ?? 15);
    totalEquipment += eqBilled;
    costEquipment += eqCost;
    subtotal += eqBilled;
    const dispCost = r2(scope.disposal);
    const dispBilled = calcScopeCost(scope.disposal, scope.disposal_gm ?? 15);
    totalDisposal += dispBilled;
    costDisposal += dispCost;
    subtotal += dispBilled;
    scopePreFee += travelBilled + eqBilled + dispBilled;
    // Drive time — kept in the labor bucket for tax math (non-taxable),
    // but surfaced separately for the display summary (iter244).
    const driveCost = r2(scope.drive_time);
    const driveBilled = calcScopeCost(scope.drive_time, scope.drive_time_gm ?? 0);
    if (driveBilled > 0) {
      totalLabor += driveBilled;
      totalDriveTime += driveBilled;
      costLabor += driveCost;
      subtotal += driveBilled;
      scopePreFee += driveBilled;
    }
    // iter260 — drive-time hours contribute to the Total Hours rollup
    // ONLY (no impact on GM / tax math — those stay driven by the $
    // cost + GM% above).
    const driveHrs = parseFloat(scope.drive_time_hours) || 0;
    if (driveHrs > 0) {
      totalHours += driveHrs;
    }
    // CC fee is rounded per scope — the same cents applyCCFeeDistribution folds into the lines.
    ccFeeAmount += r2(r2(scopePreFee) * (ccFeeP / 100));
  });
  // CC Fee — Square processing fee (defaults to 3.5%). Categorized as Material so
  // (a) it rolls into the Material total in the Summary of Costs, and
  // (b) it's included in the tax base with the rest of the material.
  ccFeeAmount = r2(ccFeeAmount);
  totalMaterial = r2(totalMaterial + ccFeeAmount);
  costMaterial = r2(costMaterial + ccFeeAmount);  // Pass-through cost — PM sees it in cost column
  subtotal = r2(subtotal + ccFeeAmount);
  totalFreight = r2(totalFreight); totalLabor = r2(totalLabor); totalEquipment = r2(totalEquipment);
  totalTravel = r2(totalTravel); totalDisposal = r2(totalDisposal); totalDriveTime = r2(totalDriveTime); totalSubcontract = r2(totalSubcontract);
  costFreight = r2(costFreight); costLabor = r2(costLabor); costEquipment = r2(costEquipment); costTravel = r2(costTravel); costDisposal = r2(costDisposal); costSubcontract = r2(costSubcontract);
  // Tax base = MATERIAL ONLY (accounting rule, Nov 2026). Freight, travel, disposal, drive time,
  // equipment, labor and subcontract are never taxed. The CC fee rides inside material.
  // Same rule as the WO / CO / invoice (`routes/_shared.line_is_taxable`).
  let taxableBase = Math.max(0, r2(totalMaterial));
  // iter257 — proposal-level discount. Always applied as a positive
  // reduction (abs value), pro-rated across taxable vs. non-taxable
  // so labor stays non-taxable and material tax reflects the
  // discounted amount. Clamped at subtotal so total can never go
  // negative.
  const dv = Math.abs(parseFloat(discountValue ?? 0) || 0);
  const dt = (discountType || "flat").toLowerCase();
  let discountAmount = dt === "pct" ? r2(subtotal * (dv / 100)) : r2(dv);
  discountAmount = Math.min(discountAmount, subtotal);
  if (subtotal > 0 && discountAmount > 0) {
    const proportion = discountAmount / subtotal;
    taxableBase = Math.max(0, r2(taxableBase - taxableBase * proportion));
  }
  let taxAmount = r2(taxableBase * (parseFloat(taxRate) || 0) / 100);
  // iter306 — manual override: a rate re-prices the live base, an exact
  // amount sticks (and is flagged stale once the base drifts).
  let taxOverrideStale = false;
  if (taxOverride?.type === "rate") {
    taxAmount = r2(taxableBase * (parseFloat(taxOverride.rate) || 0) / 100);
  } else if (taxOverride?.type === "amount") {
    taxAmount = r2(taxOverride.amount);
    taxOverrideStale = taxOverride.basis_taxable != null && Math.abs(Number(taxOverride.basis_taxable) - taxableBase) > 0.01;
  }
  const total = r2(subtotal - discountAmount + taxAmount);
  return {
    subtotal, totalMaterial, totalFreight, totalLabor, totalEquipment, totalHours, totalTravel, totalDisposal, totalDriveTime, taxAmount, total,
    totalSubcontract, costSubcontract,
    taxableBase, taxOverrideStale,
    costMaterial, costFreight, costLabor, costEquipment, costTravel, costDisposal,
    ccFeePct: ccFeeP, ccFeeAmount,
    discountAmount, discountType: dt, discountValue: dv,
  };
};

// ============== ADD LINE ITEM WITH SEARCH ==============

